import { useSessionTokenGetter } from '@develio/auth/client';
import { useEffect, useState } from 'react';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

export function PipelineStatusBanner() {
  const getToken = useSessionTokenGetter();
  const [state, setState] = useState<{ isRunning: boolean; everRan: boolean } | null>(null);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API}/api/v1/pipeline-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok && !cancelled) setState(await res.json());
      } catch {
        /* non-fatal */
      }
    };
    check();
    // Poll every 15s while visible
    const interval = setInterval(check, 15000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [getToken]);

  if (!state || (state.everRan && !state.isRunning)) return null;

  if (state.isRunning) {
    return (
      <div className="mb-6 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
        <svg
          className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-blue-500"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
          />
        </svg>
        <div>
          <p className="font-medium">Market intelligence data is loading</p>
          <p className="mt-0.5 text-blue-700">
            We're pulling the latest BLS data for your area. This usually takes 1–2 minutes. This
            page will update automatically.
          </p>
        </div>
      </div>
    );
  }

  // Never ran
  return (
    <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
      <svg
        className="mt-0.5 h-4 w-4 shrink-0 text-amber-500"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
        />
      </svg>
      <div>
        <p className="font-medium">Local data not yet loaded</p>
        <p className="mt-0.5 text-amber-700">
          Contact your administrator to initialize your market intelligence data.
        </p>
      </div>
    </div>
  );
}
